import './DashboardButtons.sass';
import { classNames as cn, formatCurrency } from '$utils';
import React, { FC, useMemo, useState } from 'react';

import { ReactComponent as IcWallet28 } from './assets/wallet_28.svg';
import { ReactComponent as IcLoudSpeaker28 } from './assets/loudspeaker_28.svg';
import { ReactComponent as IcMore28 } from './assets/more_28.svg';
import { ReactComponent as IcPlusCircle28 } from './assets/plus_circle_28.svg';
import { ReactComponent as IcUserPlus28 } from './assets/user_plus_28.svg';
import { ReactComponent as IcUserQuestionmark28 } from './assets/user_questionmark_28.svg';
import { ReactComponent as IcUserSponsor28 } from './assets/user_sponsor_28.svg';
import { ReactComponent as IcList28 } from 'assets/28/list_28.svg';
import { ReactComponent as IcGroups28 } from 'assets/28/groups_28.svg';
import { ReactComponent as IcCloud28 } from './assets/cloud_28.svg';

import { Link } from 'react-router5';
import { showModal } from '$navigation/helper';
import {
  ADS,
  CLOUD,
  INVITE,
  LISTS,
  INCOGNITO,
  MINE,
  MINE_SQUAD,
  MINE_TOP,
  NOTIFY,
  OFFERS,
  OTHER,
  PERSONAL_SUBSCRIPTION_INFO,
  TELEGRAM,
} from '$shared/constants/pages';
import { mainActions, mainSelector, managerLinkSelector } from '$store/main';
import { useDispatch, useSelector } from 'react-redux';
import { balanceSelector } from '$store/balance';
import { Currency, MetaTags } from '$shared/components';
import { CurrencyEnum } from '$store/models';
import { inviteSelector } from '$store/invite';
import { PersonalManagerBottomSheet } from '$core';
import { useCurrentUserInfo, useTranslator } from '$hooks';
import { IcNotifications28, IcPickaxe28, IcTelegram24, IcUsers28 } from '$assets';
import { dashboardSelector } from '$store/dashboard';

const COMMUNITY_LINK = 'https://t.me/+8x4dcZ-Ov-IxNjJi';

export const DashboardButtons: FC = () => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const { eurBalance, holdBalance } = useSelector(balanceSelector);
  const { squadId } = useSelector(dashboardSelector);
  const { totalInvitedFriends: invited } = useSelector(inviteSelector);
  const managerLink = useSelector(managerLinkSelector);
  const [showManagerBottomSheet, setShowManagerBottomSheet] = useState(false);
  const user = useCurrentUserInfo();
  const { hasNotify } = useSelector(mainSelector);

  const invitedString = useMemo(() => {
    if (invited > 1000) {
      return Math.round(invited / 1000) + 'K';
    }
    return invited.toString();
  }, [invited]);

  const balanceString = useMemo(
    () => formatCurrency(eurBalance, CurrencyEnum.EUR),
    [eurBalance],
  );

  return (
    <>
      <MetaTags title={t('dashboard_title')} />
      {showManagerBottomSheet && (
        <PersonalManagerBottomSheet onClose={() => setShowManagerBottomSheet(false)} />
      )}
      <div className="DashboardButtons">
        <div className="DashboardButtons__grid">
          <div
            className="DashboardButtons__grid__item large"
            onClick={() => showModal('balance', false, { currency: 'eur' })}
          >
            <IcWallet28 />
            <div className="DashboardButtons__grid__item__content">
              <div
                className={cn('DashboardButtons__grid__item__primary', {
                  small: balanceString.length > 6,
                  extraSmall: balanceString.length > 9,
                })}
              >
                {balanceString}
              </div>
              <div className="DashboardButtons__grid__item__secondary">
                {t('dashboard_button_pending')}{' '}
                <strong>
                  <Currency amount={holdBalance} currency={CurrencyEnum.EUR} />
                </strong>
              </div>
            </div>
            <div className="DashboardButtons__grid__item__label">
              {t('dashboard_button_balance')}
            </div>
          </div>
          {!user?.isolated && (
            <Link routeName={ADS} className="DashboardButtons__grid__item">
              <IcLoudSpeaker28 />
              <div className="DashboardButtons__grid__item__label">
                {t('dashboard_button_ads')}
              </div>
            </Link>
          )}
          <Link
            routeName={PERSONAL_SUBSCRIPTION_INFO}
            className="DashboardButtons__grid__item"
          >
            <IcUserSponsor28 />
            <div className="DashboardButtons__grid__item__label">
              {t('dashboard_button_sponsors')}
            </div>
          </Link>
          <Link routeName={CLOUD} className="DashboardButtons__grid__item">
            <IcCloud28 />
            <div className="DashboardButtons__grid__item__label">
              {t('dashboard_button_cloud')}
            </div>
          </Link>
          <Link routeName={LISTS} className="DashboardButtons__grid__item">
            <IcList28 />
            <div className="DashboardButtons__grid__item__label">
              {t('dashboard_button_list')}
            </div>
          </Link>
          <div
            onClick={() => dispatch(mainActions.setCreateMenuShown(true))}
            className="DashboardButtons__grid__item"
          >
            <IcPlusCircle28 />
            <div className="DashboardButtons__grid__item__label">
              {t('dashboard_button_add')}
            </div>
          </div>
          <Link routeName={INVITE} className="DashboardButtons__grid__item">
            {invited ? (
              <div
                className={cn('DashboardButtons__grid__item__count', {
                  one: invitedString.length === 1,
                  two: invitedString.length === 2,
                  three: invitedString.length > 2,
                })}
              >
                {invitedString}
              </div>
            ) : (
              <IcUserPlus28 />
            )}
            <div className="DashboardButtons__grid__item__label">
              {t('dashboard_button_referrals')}
            </div>
          </Link>
          {managerLink && (
            <div
              onClick={() => setShowManagerBottomSheet(true)}
              className="DashboardButtons__grid__item"
            >
              <IcUserQuestionmark28 />
              <div className="DashboardButtons__grid__item__label">
                {t('dashboard_button_manager')}
              </div>
            </div>
          )}
          <Link routeName={NOTIFY} className="DashboardButtons__grid__item">
            <IcNotifications28 />
            {hasNotify && <div className="DashboardButtons__grid__item_badge" />}
            <div className="DashboardButtons__grid__item__label">
              {t('navbar_notify')}
            </div>
          </Link>
          {/* <Link routeName={TELEGRAM} className="DashboardButtons__grid__item">
            <IcTelegram24 />
            <div className="DashboardButtons__grid__item__label">Telegram</div>
          </Link> */}
          <a
            href={COMMUNITY_LINK}
            target="_blank"
            rel="noreferrer"
            className="DashboardButtons__grid__item community"
          >
            <div className="DashboardButtons__grid__item__live">Live</div>
            <IcGroups28 />
            <div className="DashboardButtons__grid__item__label">Community</div>
          </a>
          <Link routeName={OFFERS} className="DashboardButtons__grid__item">
            <IcUsers28 />
            <div className="DashboardButtons__grid__item__label">
              {t('navbar_offers')}
            </div>
          </Link>
          {squadId > 0 ? (
            <Link
              routeName={MINE_SQUAD}
              routeParams={{ id: squadId }}
              className="DashboardButtons__grid__item"
            >
              <IcPickaxe28 />
              <div className="DashboardButtons__grid__item__label">Mining</div>
            </Link>
          ) : (
            <Link routeName={MINE_TOP} className="DashboardButtons__grid__item">
              <IcPickaxe28 />
              <div className="DashboardButtons__grid__item__label">Mining</div>
            </Link>
          )}
          <Link routeName={OTHER} className="DashboardButtons__grid__item">
            <IcMore28 />
            <div className="DashboardButtons__grid__item__label">
              {t('dashboard_button_other')}
            </div>
          </Link>
        </div>
      </div>
    </>
  );
};

// deploy
